/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useState } from 'react';
import { EntryPointContainer } from 'react-relay';
import { useIsHydrateRenderMode } from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import { JiraAtlassianNavigationSkeleton as TopNavigationSkeleton } from '@jira/navigation-apps__atlassian-navigation-skeleton/src/index.tsx';
import { TopNavigation } from '@jira/navigation-apps__atlassian-navigation/src/ui/TopNavigation.tsx';
import { JS_EVAL_START } from '@jira/platform__common-constants/src/analytics-marks.js';
import { TOP_NAVIGATION } from '@jira/platform__entry-points-page-layout-fields/src/index.tsx';
import { usePageLayoutEntryPoint } from '@jira/platform__entry-points-page-layout-entry-point/src/index.tsx';
import { EntryPointReferencePlaceholder } from '@jira/platform__entry-points-point-placeholder/src/index.tsx';
import { stopInitialPageLoadTimingFromPerformanceMarkStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/performance-marks-tools/index.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import { ReportSSRFailOnUse } from '@jira/ssr__rendering-guard/src/index.tsx';
import { topNavEntryPoint } from '@jira/navigation-apps__atlassian-navigation/src/topNavEntryPoint.entrypoint.tsx';

/**
 * Manages the rendering of the navigation component by conditionally displaying either a placeholder
 * or the actual navigation content. This decision is based on the current routing context and
 * the availability of navigation entry points, aiming to enhance the user experience through
 * efficient loading strategies.
 */
export const AsyncAtlassianNavigation = (): React.JSX.Element => {
	const topNavEntryPointReference =
		usePageLayoutEntryPoint<typeof topNavEntryPoint>(TOP_NAVIGATION);

	useState(() => {
		if (!__SERVER__ && topNavEntryPointReference) {
			topNavEntryPoint.root
				// @ts-expect-error - TS2339 - We're using JSResourceReference from @atlassian/react-async.
				.onComplete(() => {
					stopInitialPageLoadTimingFromPerformanceMarkStart(
						'navigation-primary.bundle-ready',
						'jira-spa.js:eval-stop',
					);
					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					const resources = performance.getEntriesByType('mark');
					for (let i = resources.length - 1; i >= 0; i -= 1) {
						if (
							(resources[i].name.startsWith('async-navigation') ||
								resources[i].name.startsWith('async-atlassian-navigation')) &&
							resources[i].name.endsWith(JS_EVAL_START)
						) {
							stopInitialPageLoadTimingFromPerformanceMarkStart(
								'navigation-primary.eval',
								resources[i].name,
							);
							break;
						}
					}
				});
		}
		return undefined;
	});

	const currentRoute = useCurrentRoute();
	const isPageHydrating = useIsHydrateRenderMode();

	return topNavEntryPointReference ? (
		// There is already a Placeholder in parent
		// remove this one when EntryPointPlaceholder merges with Placeholder
		<EntryPointReferencePlaceholder
			name="top_navigation_skeleton"
			fallback={
				<>
					<TopNavigationSkeleton />
					<ReportSSRFailOnUse
						pageId="navigation"
						fallbackId="TopNavEntryPoint"
						displayErrorInStaging
					/>
				</>
			}
			entryPointReference={topNavEntryPointReference}
			isSSRPlaceholderEnabled={
				!isPageHydrating ? currentRoute.entryPointOptions?.isSSRPlaceholderEnabled : undefined
			}
		>
			{topNavEntryPointReference ? (
				<EntryPointContainer
					entryPointReference={topNavEntryPointReference}
					// oxlint-disable-next-line typescript/consistent-type-assertions
					props={{} as never}
				/>
			) : null}
		</EntryPointReferencePlaceholder>
	) : (
		<TopNavigation />
	);
};
